import React from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ArrowRight, Mail } from "lucide-react";

export default function Refusjon() {
  return (
    <div data-testid="page-refusjon">
      {/* Hero */}
      <section className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#D97B66]"></span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
              Juridisk
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-[#2C302E] leading-[1.05]">
            Refusjons&shy;policy
          </h1>
          <p className="mt-6 text-base text-[#6B726C]">
            Sist oppdatert: 1. februar 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="space-y-10 text-[#2C302E]">

          <div className="flex items-start gap-4 p-6 bg-[#E5E2DC]/60 rounded-2xl">
            <RefreshCw className="w-6 h-6 text-[#6B726C] mt-1 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#2C302E] leading-relaxed m-0">
              Småstund selger digitale PDF-produkter som leveres umiddelbart etter betaling.
              Denne siden forklarer når du kan få refusjon, og hvordan du går frem.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Digitale produkter og angrerett</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              I henhold til <strong>angrerettloven § 22 bokstav n</strong> faller angreretten bort for
              digitale leveranser som starter umiddelbart, når kunden uttrykkelig har samtykket og erkjent
              å miste angreretten. Ved å fullføre kjøpet og laste ned filene gir du dette samtykket.
            </p>
            <p className="text-[#4A4F4A] leading-relaxed mt-3">
              Dette betyr at vi som hovedregel <strong>ikke gir refusjon</strong> på digitale produkter
              som allerede er levert og tilgjengeliggjort for nedlasting.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Når vi gir refusjon</h2>
            <p className="text-[#4A4F4A] leading-relaxed mb-3">
              Vi gir likevel refusjon i følgende tilfeller:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>Filen er <strong>ødelagt</strong> eller lar seg ikke åpne, og vi klarer ikke å sende deg en
                fungerende erstatningsfil innen rimelig tid.</li>
              <li>Innholdet samsvarer <strong>ikke med beskrivelsen</strong> på produktsiden.</li>
              <li>Du har <strong>kjøpt samme produkt to ganger</strong> ved en feil.</li>
              <li>Tekniske feil på vår side gjør at du ikke får tilgang til produktet.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Slik søker du om refusjon</h2>
            <ol className="list-decimal pl-6 space-y-3 text-[#4A4F4A]">
              <li>Send e-post til <a href="mailto:kontakt@småstund.no" className="text-[#D97B66] underline">kontakt@småstund.no</a> innen 14 dager etter kjøpet.</li>
              <li>Oppgi ordrenummer (finnes i bekreftelses-e-posten din).</li>
              <li>Beskriv kort hva som er feil — gjerne med et skjermbilde.</li>
              <li>Vi svarer innen 3 virkedager og avtaler videre.</li>
            </ol>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Behandlingstid</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Godkjente refusjoner overføres tilbake til samme betalingsmiddel innen 5–10 virkedager.
              Behandlingstiden hos banken/Stripe kan komme i tillegg.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Garanti om kvalitet</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Vi er stolte av ressursene våre og ønsker at du blir fornøyd. Er det noe du lurer på før du
              kjøper, eller etter at du har lastet ned, ta kontakt — vi hjelper deg gjerne videre.
            </p>
          </div>

          {/* Contact card */}
          <div className="mt-12 bg-[#2C302E] text-white rounded-3xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-5 h-5 text-[#D97B66]" strokeWidth={1.5} />
              <p className="text-xs uppercase tracking-[0.25em] text-white/60 font-medium m-0">
                Kontakt oss
              </p>
            </div>
            <p className="font-heading text-2xl md:text-3xl font-light mb-2">
              Spørsmål om bestillingen din?
            </p>
            <p className="text-[#A8A8A0] leading-relaxed mb-5">
              Vi svarer som regel innen ett døgn på hverdager.
            </p>
            <a
              href="mailto:kontakt@småstund.no"
              data-testid="refusjon-contact-email"
              className="inline-flex items-center gap-2 bg-[#D97B66] hover:bg-[#C66A55] text-white rounded-full px-6 py-3 font-medium transition-colors"
            >
              kontakt@småstund.no
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

        </div>

        {/* Back link */}
        <div className="mt-16 pt-10 border-t border-[#E5E2DC]">
          <Link
            to="/"
            data-testid="refusjon-back-home"
            className="inline-flex items-center gap-2 text-[#D97B66] hover:text-[#C66A55] font-medium"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Tilbake til forsiden
          </Link>
        </div>
      </section>
    </div>
  );
}
